import { useCallback, useState, useEffect } from 'react'
import { useConnect, useAccount, useSignMessage, type Connector } from 'wagmi'
import { getChainId } from 'wagmi/actions'
import { TARGET_CHAIN_ID } from './contract'
import { wagmiConfig, switchNetworkInBinance } from './wallet'

export interface WalletState {
  isConnected: boolean
  isConnecting: boolean
  address: string | undefined
  error: Error | null
  connect: () => void
  signMessage: (message?: string) => Promise<string | undefined>
}

// Helper function to find Binance wallet connector
const findBinanceConnector = (connectors: readonly Connector[]) => {
  // 将所有 connector.id 输出到 alert
  const connectorIds = connectors.map(connector => connector.id).join(', ');
  // alert(`connectors: ${connectorIds}`)
  console.log('connectorIds :>> ', connectorIds);
  
  // 尝试匹配所有可能的币安钱包连接器ID
  const possibleBinanceIds = ['BinanceW3WSDK', 'com.binance.wallet', 'wallet.binance.com'];
  
  // 检查是否存在任何匹配的连接器
  const foundConnector = connectors.find(connector => {
    const match = possibleBinanceIds.includes(connector.id);
    console.log(`检查连接器: ${connector.id}, 匹配: ${match}`);
    return match;
  });
  
  // 如果没有找到匹配的连接器，尝试使用第一个可用的连接器
  if (!foundConnector && connectors.length > 0) {
    console.log(`未找到币安连接器，尝试使用第一个可用连接器: ${connectors[0].id}`);
    return connectors[0];
  }
  
  return foundConnector;
}

export function useWallet(): WalletState {
  const { connectors, connect: wagmiConnect, isPending } = useConnect()
  const { address, isConnected } = useAccount()
  const { signMessage: wagmiSignMessage, data: signatureData } = useSignMessage()
  const [error, setError] = useState<Error | null>(null)
  const [hasTriedAutoConnect, setHasTriedAutoConnect] = useState(false)

  // 自动切换到目标网络
  useEffect(() => {
    const autoSwitchNetwork = async () => {
      if (!isConnected) return
      
      try {
        const currentChainId = getChainId(wagmiConfig)
        console.log('当前链ID:', currentChainId, '目标链ID:', TARGET_CHAIN_ID)
        console.log('强行切到 bsc 链条上: switchNetworkInBinance ');
        await switchNetworkInBinance(TARGET_CHAIN_ID)
      } catch (error) {
        console.error('自动切换网络失败:', error)
        // 只在用户主动操作时显示错误，自动切换失败不影响正常使用
        if (error instanceof Error && !error.message.includes('User rejected')) {
          console.warn('网络切换失败，但不影响使用。错误:', error.message)
        }
      }
    }

    autoSwitchNetwork()
  }, [isConnected])

  // 连接币安钱包
  const connect = useCallback(() => {
    try {
      console.log('尝试连接钱包...');
      setError(null)
      const binanceConnector = findBinanceConnector(connectors)
      
      if (binanceConnector) {
        console.log(`使用连接器 ${binanceConnector.id} 连接钱包`);
        // 添加连接选项，确保在币安内置浏览器中正常工作
        try {
          wagmiConnect({ 
            connector: binanceConnector
          });
          console.log('钱包连接请求已发送');
        } catch (error: unknown) {
          console.error('钱包连接失败:', error);
          const errorObj = error instanceof Error ? error : new Error(`Failed to connect wallet: ${String(error)}`);
          setError(errorObj);
        }
      } else {
        const errorMsg = '未找到币安钱包连接器';
        console.error(errorMsg);
        setError(new Error(errorMsg));
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '连接钱包失败';
      console.error('连接钱包出错:', errorMsg);
      setError(err instanceof Error ? err : new Error(errorMsg));
    }
  }, [connectors, wagmiConnect])

  // 自动连接钱包
  useEffect(() => {
    // 如果已经连接、正在连接或已经尝试过自动连接，则不执行
    if (isConnected || isPending || hasTriedAutoConnect) {
      return;
    }

    // 确保connectors已经加载
    if (connectors.length === 0) {
      return;
    }

    // 延迟一点时间，确保wagmi完全初始化
    const timer = setTimeout(() => {
      console.log('正在自动连接钱包...');
      setHasTriedAutoConnect(true);
      connect();
    }, 800);

    return () => clearTimeout(timer);
  }, [isConnected, isPending, hasTriedAutoConnect, connectors.length, connect]);

  // 签名消息
  const signMessage = useCallback(
    async (message = 'Welcome to PIN AI TGE DApp') => {
      if (!isConnected) {
        setError(new Error('Wallet not connected'))
        return undefined
      }
      
      try {
        await wagmiSignMessage({ message })
        return signatureData
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to sign message'))
        return undefined
      }
    },
    [isConnected, wagmiSignMessage, signatureData]
  )

  return {
    isConnected,
    isConnecting: isPending,
    address,
    error,
    connect,
    signMessage
  }
}
